import React from "react";
import { Head } from "@unhead/react";
import { Link } from "react-router-dom";

export default function StrikeBitcoinDca() {
  const canonical =
    "https://satstacker.app/strike-bitcoin-dca";

  const title =
    "Bitcoin DCA with Strike + SatStacker Smart Timing";

  const description =
    "Use SatStacker with your existing Strike account to automate Bitcoin DCA with Smart Timing. SatStacker watches each buy window, looks for dips, and uses Strike to execute the purchase.";

  const faqItems = [
    {
      q: "Does SatStacker replace Strike?",
      a: "No. SatStacker works with your existing Strike account. Strike handles the Bitcoin purchase and account infrastructure, while SatStacker adds the recurring schedule, buying windows, and Smart Timing logic.",
    },
    {
      q: "How does SatStacker work with Strike?",
      a: "You connect your Strike account securely through OAuth. SatStacker then manages the timing of your recurring Bitcoin plan and uses Strike to execute purchases when Smart Timing rules trigger.",
    },
    {
      q: "What is Smart Timing?",
      a: "Smart Timing is SatStacker's rules-based approach to recurring Bitcoin DCA. Instead of automatically buying at one fixed moment, SatStacker watches price movement inside each scheduled buy window and can execute when its timing rules detect a qualifying opportunity.",
    },
    {
      q: "Does SatStacker buy the dip?",
      a: "SatStacker watches each buy window and buys the dip when its Smart Timing rules trigger. If no qualifying dip appears, the remaining planned amount is executed before the window closes.",
    },
    {
      q: "Does SatStacker hold my cash or Bitcoin?",
      a: "No. SatStacker itself does not hold your cash or Bitcoin. Purchases are executed through your connected Strike account. Bitcoin can remain with Strike or be withdrawn according to the options available to you.",
    },
    {
      q: "Does Smart Timing guarantee more Bitcoin?",
      a: "No. Smart Timing does not guarantee a better execution price in every buying window. Some completed windows can perform better than scheduled DCA and others can perform worse.",
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "SatStacker",
        item: "https://satstacker.app/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Bitcoin DCA with Strike",
        item: canonical,
      },
    ],
  };

  return (
    <main className="min-h-screen bg-zinc-950 text-white">
      <Head>
        <title>{title} | SatStacker</title>

        <meta
          name="description"
          content={description}
        />

        <link
          rel="canonical"
          href={canonical}
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          property="og:title"
          content={title}
        />

        <meta
          property="og:description"
          content={description}
        />

        <meta
          property="og:url"
          content={canonical}
        />

        <meta
          property="og:site_name"
          content="SatStacker"
        />

        <meta
          name="twitter:card"
          content="summary"
        />

        <meta
          name="twitter:title"
          content={title}
        />

        <meta
          name="twitter:description"
          content={description}
        />

        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Head>

      <div className="max-w-5xl mx-auto px-5 py-12 md:py-16">

        {/* HERO */}
        <header className="max-w-3xl mb-16">
          <p className="text-xs uppercase tracking-[0.18em] text-orange-400 font-bold mb-4">
            SatStacker + Strike
          </p>

          <h1 className="text-4xl md:text-6xl font-black leading-[1.08] mb-6">
            Bitcoin DCA with Strike.
            <span className="block text-orange-400 mt-2">
              Smarter Timing with SatStacker.
            </span>
          </h1>

          <p className="text-lg md:text-xl text-zinc-300 leading-8 mb-5">
            SatStacker connects to your existing Strike
            account and adds automated, dip-aware Smart
            Timing to your recurring Bitcoin DCA.
          </p>

          <p className="text-zinc-400 leading-7 max-w-2xl">
            You choose the amount and cadence. SatStacker
            watches each buy window, looks for better
            Bitcoin prices, and uses Strike to execute the
            purchase when its Smart Timing rules trigger.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link
              to="/signup"
              className="rounded-xl bg-orange-500 px-6 py-3.5 text-center font-bold text-white hover:bg-orange-600 transition"
            >
              Start Stacking →
            </Link>

            <Link
              to="/smart-timing-results"
              className="rounded-xl border border-zinc-700 px-6 py-3.5 text-center font-bold text-white hover:border-zinc-500 transition"
            >
              See Live Smart Timing Results
            </Link>
          </div>
        </header>

        {/* SIMPLE POSITIONING */}
        <section className="mb-16">
          <div className="rounded-3xl border border-orange-500/20 bg-orange-500/5 p-6 md:p-8">
            <p className="text-sm uppercase tracking-widest text-orange-400 font-bold mb-3">
              The simple version
            </p>

            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Strike handles the Bitcoin purchase.
              SatStacker handles the timing.
            </h2>

            <p className="text-zinc-300 leading-7 max-w-3xl">
              SatStacker is not an exchange and does not
              replace your Strike account. It sits on top
              of your existing Bitcoin buying setup and
              automates when your planned recurring
              purchase executes inside each buying window.
            </p>
          </div>
        </section>

        {/* WHAT EACH PRODUCT DOES */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8">
            What Strike does vs. what SatStacker does
          </h2>

          <div className="grid md:grid-cols-2 gap-5">

            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6">
              <p className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-3">
                Strike
              </p>

              <h3 className="text-xl font-bold mb-5">
                Purchase infrastructure
              </h3>

              <ul className="space-y-3 text-zinc-400 leading-6">
                <li>
                  • Your connected Bitcoin account
                </li>

                <li>
                  • Available cash balance and funding
                </li>

                <li>
                  • Bitcoin purchase execution
                </li>

                <li>
                  • Bitcoin account and withdrawal
                  infrastructure
                </li>
              </ul>
            </div>

            <div className="rounded-2xl border border-orange-500/25 bg-orange-500/5 p-6">
              <p className="text-xs uppercase tracking-widest text-orange-400 font-bold mb-3">
                SatStacker
              </p>

              <h3 className="text-xl font-bold mb-5">
                Automation + Smart Timing
              </h3>

              <ul className="space-y-3 text-zinc-300 leading-6">
                <li>
                  • Your recurring Bitcoin DCA schedule
                </li>

                <li>
                  • Daily, weekly, or bi-weekly buying
                  windows
                </li>

                <li>
                  • Rules-based Smart Timing
                </li>

                <li>
                  • Automated dip-aware execution
                </li>

                <li>
                  • Performance and buying-window tracking
                </li>
              </ul>
            </div>

          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="mb-16">
          <p className="text-xs uppercase tracking-[0.16em] text-orange-400 font-bold mb-3">
            How it works
          </p>

          <h2 className="text-3xl font-bold mb-8">
            From recurring budget to Bitcoin purchase
          </h2>

          <div className="space-y-4">

            <Step
              number="01"
              title="Connect your Strike account"
            >
              SatStacker connects securely to your existing
              Strike account through OAuth. You do not need
              to create another exchange account for
              SatStacker.
            </Step>

            <Step
              number="02"
              title="Choose your Bitcoin DCA"
            >
              Set how much Bitcoin you want to buy and
              choose a daily, weekly, or bi-weekly cadence.
            </Step>

            <Step
              number="03"
              title="Smart Timing watches the window"
            >
              Instead of treating one predetermined moment
              as the only possible entry, SatStacker
              watches price movement during the buying
              window.
            </Step>

            <Step
              number="04"
              title="SatStacker buys the dip"
            >
              When Smart Timing rules trigger, SatStacker
              uses your connected Strike account to execute
              the planned Bitcoin purchase.
            </Step>

            <Step
              number="05"
              title="The window still gets completed"
            >
              If no qualifying Smart Timing opportunity
              appears, the remaining planned amount is
              executed before the buying window closes.
            </Step>

          </div>
        </section>

        {/* DCA COMPARISON */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-4">
            Scheduled DCA vs. Smart Timing
          </h2>

          <p className="text-zinc-400 leading-7 mb-8 max-w-3xl">
            Both approaches can use the same planned budget
            and recurring cadence. The difference is what
            happens inside the buying window.
          </p>

          <div className="overflow-x-auto rounded-2xl border border-zinc-800">
            <table className="w-full text-left">
              <thead className="bg-zinc-900">
                <tr>
                  <th className="p-4 text-sm text-zinc-500">
                    Feature
                  </th>

                  <th className="p-4 text-sm text-zinc-300">
                    Scheduled DCA
                  </th>

                  <th className="p-4 text-sm text-orange-400">
                    SatStacker Smart Timing
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-zinc-800">
                <ComparisonRow
                  label="Recurring budget"
                  regular="Yes"
                  smart="Yes"
                />

                <ComparisonRow
                  label="Recurring cadence"
                  regular="Yes"
                  smart="Yes"
                />

                <ComparisonRow
                  label="Execution timing"
                  regular="Scheduled time"
                  smart="Inside the buying window"
                />

                <ComparisonRow
                  label="Looks for dips"
                  regular="No timing layer"
                  smart="Yes"
                />

                <ComparisonRow
                  label="Automated"
                  regular="Yes"
                  smart="Yes"
                />

                <ComparisonRow
                  label="Guarantees better results"
                  regular="No"
                  smart="No"
                />
              </tbody>
            </table>
          </div>
        </section>

        {/* WHY WINDOWS */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-5">
            Why does SatStacker use buying windows?
          </h2>

          <p className="text-zinc-400 leading-7 mb-4">
            Traditional DCA solves one problem: deciding
            how much to buy and how often. But Bitcoin can
            move meaningfully between the start and end of
            a recurring period.
          </p>

          <p className="text-zinc-400 leading-7 mb-4">
            Smart Timing keeps the discipline of a
            recurring Bitcoin plan while giving the
            purchase time to look for a better entry
            inside that window.
          </p>

          <p className="text-zinc-400 leading-7">
            That does not mean every Smart Timing window
            will beat scheduled DCA. Some windows perform
            better and some perform worse. The goal is to
            improve execution over time without turning
            Bitcoin accumulation into manual trading.
          </p>
        </section>

        {/* LIVE EVIDENCE */}
        <section className="mb-16">
          <div className="rounded-3xl border border-zinc-800 bg-zinc-900/40 p-7 md:p-9">

            <p className="text-xs uppercase tracking-widest text-orange-400 font-bold mb-3">
              Real results
            </p>

            <h2 className="text-3xl font-bold mb-4">
              See how Smart Timing is actually performing
            </h2>

            <p className="text-zinc-400 leading-7 mb-6 max-w-3xl">
              SatStacker's live results page compares
              completed Smart Timing buying windows
              against scheduled DCA using the same
              post-SatStacker-fee buying power. Winning,
              losing, and neutral completed windows are
              included.
            </p>

            <Link
              to="/smart-timing-results"
              className="inline-block rounded-xl bg-orange-500 px-5 py-3 font-bold text-white hover:bg-orange-600"
            >
              View Live Smart Timing Results →
            </Link>

          </div>
        </section>

        {/* CUSTODY */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-5">
            Does SatStacker hold your Bitcoin?
          </h2>

          <p className="text-zinc-400 leading-7 mb-4">
            No. SatStacker itself does not hold your cash
            or Bitcoin.
          </p>

          <p className="text-zinc-400 leading-7 mb-4">
            Your Bitcoin purchases are executed through
            your connected Strike account. SatStacker
            provides the automation and timing layer.
          </p>

          <p className="text-zinc-400 leading-7">
            After purchase, Bitcoin can remain with Strike
            or be withdrawn according to the options
            available through your account and SatStacker
            setup.
          </p>
        </section>

        {/* LINKS */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-7">
            Learn more about Smart Timing
          </h2>

          <div className="grid md:grid-cols-2 gap-4">

            <ResourceLink
              to="/smart-dca"
              title="What is Smart DCA?"
              text="Learn how SatStacker's buying windows and Smart Timing work."
            />

            <ResourceLink
              to="/smart-timing-results"
              title="Live Smart Timing Results"
              text="See aggregate results across completed buying windows."
            />

            <ResourceLink
              to="/blog/smart-dca-vs-regular-dca"
              title="3-Month Case Study"
              text="See a historical Smart Timing vs. regular DCA comparison."
            />

            <ResourceLink
              to="/calculator"
              title="Bitcoin DCA Calculator"
              text="Test different amounts and recurring schedules using historical Bitcoin data."
            />

          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-7">
            SatStacker + Strike FAQ
          </h2>

          <div className="space-y-4">
            {faqItems.map((item) => (
              <div
                key={item.q}
                className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6"
              >
                <h3 className="text-lg font-bold mb-3">
                  {item.q}
                </h3>

                <p className="text-zinc-400 leading-7 m-0">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="rounded-3xl border border-orange-500/25 bg-orange-500/5 p-8 md:p-10 text-center">
          <p className="text-xs uppercase tracking-widest text-orange-400 font-bold mb-3">
            Smarter Bitcoin DCA
          </p>

          <h2 className="text-3xl md:text-4xl font-black mb-4">
            Same recurring habit.
            <span className="block text-orange-400">
              Smarter timing.
            </span>
          </h2>

          <p className="text-zinc-400 leading-7 max-w-xl mx-auto mb-7">
            Connect your Strike account and let SatStacker
            watch each buying window automatically.
          </p>

          <Link
            to="/signup"
            className="inline-block rounded-xl bg-orange-500 px-7 py-3.5 font-bold text-white hover:bg-orange-600"
          >
            Start Stacking →
          </Link>

          <p className="text-xs text-zinc-600 mt-5">
            Smart Timing does not guarantee better results
            in every buying window.
          </p>
        </section>

      </div>
    </main>
  );
}

function Step({ number, title, children }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/30 p-6">
      <div className="flex gap-5">
        <div className="text-orange-400 font-black text-sm pt-1">
          {number}
        </div>

        <div>
          <h3 className="text-lg font-bold mb-2">
            {title}
          </h3>

          <p className="text-zinc-400 leading-7 m-0">
            {children}
          </p>
        </div>
      </div>
    </div>
  );
}

function ComparisonRow({
  label,
  regular,
  smart,
}) {
  return (
    <tr>
      <td className="p-4 text-sm text-zinc-400 font-medium">
        {label}
      </td>

      <td className="p-4 text-sm text-zinc-400">
        {regular}
      </td>

      <td className="p-4 text-sm text-zinc-200 font-medium">
        {smart}
      </td>
    </tr>
  );
}

function ResourceLink({
  to,
  title,
  text,
}) {
  return (
    <Link
      to={to}
      className="block rounded-2xl border border-zinc-800 bg-zinc-900/30 p-6 hover:border-orange-500/30 transition"
    >
      <h3 className="font-bold text-white mb-2">
        {title}
      </h3>

      <p className="text-sm text-zinc-500 leading-6 mb-3">
        {text}
      </p>

      <span className="text-sm font-bold text-orange-400">
        Learn more →
      </span>
    </Link>
  );
}